import { useEffect, useState } from 'react'
import { useAppState, useAppDispatch } from './Context'
import { IconFitlerGroupOpen, IconFitlerGroupClose } from '@react/common/icons'

function TopLevelFilter({ filter }) {
  const dispatch = useAppDispatch()
  const { selectedFilters } = useAppState()

  const handleChange = (e) => {
    dispatch({ type: 'toggleFilter', payload: e.target.value })
  }

  return (
    <div className="py-2 px-6 border-b md:py-3 xl:py-4 border-gray">
      <label
        htmlFor={`filter-${filter.id}`}
        className="flex gap-2 justify-between items-center w-full transition-colors cursor-pointer xl:gap-3 filter-item hover:text-green"
      >
        <input
          id={`filter-${filter.id}`}
          type="checkbox"
          value={filter.id}
          onChange={handleChange}
          checked={selectedFilters.includes(filter.id)}
        />
        <span className="flex-shrink-0 order-last styled-checkbox" aria-hidden="true">
          <span className="block relative mx-auto w-5 h-5 border top-[0.2em] rounded-[3px] border-gray-dark xl:top-[0.1em]" />
        </span>

        <span className="flex gap-2 items-center font-semibold">
          <span
            dangerouslySetInnerHTML={{ __html: filter.icon }}
            className="flex justify-center items-center w-12 h-7 filter-icon text-green"
          />

          {filter.title}
        </span>
      </label>
    </div>
  )
}

function ChildFilter({ filter }) {
  const dispatch = useAppDispatch()
  const { selectedFilters } = useAppState()

  const handleChange = (e) => {
    dispatch({ type: 'toggleFilter', payload: e.target.value })
  }

  return (
    <div>
      <label
        htmlFor={`filter-${filter.id}`}
        className="flex items-baseline w-full transition-colors cursor-pointer filter-item hover:text-green"
      >
        <input
          id={`filter-${filter.id}`}
          type="checkbox"
          value={filter.id}
          onChange={handleChange}
          checked={selectedFilters.includes(filter.id)}
        />
        <span className="flex-shrink-0 w-12 styled-checkbox" aria-hidden="true">
          <span className="block relative mx-auto w-4 h-4 border top-[0.2em] rounded-[3px] border-gray-dark xl:top-[0.09em]" />
        </span>

        <span>{filter.title}</span>
      </label>
    </div>
  )
}

function TopLevelCategory({ filter, openChildren, onToggle }) {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(openChildren.includes(parseInt(filter.id)))
  }, [openChildren])

  return (
    <div>
      <button
        type="button"
        className="flex justify-between items-center py-2 px-6 w-full border-b transition-colors md:py-3 xl:py-4 border-gray hover:text-green"
        onClick={() => onToggle(filter.id, !open)}
        aria-controls={`filter-group-${filter.id}`}
        aria-expanded={`${open ? 'true' : 'false'}`}
      >
        <span className="flex gap-2 items-center font-semibold">
          <span
            dangerouslySetInnerHTML={{ __html: filter.icon }}
            className="flex justify-center items-center w-12 h-7 filter-icon text-green"
          />

          {filter.title}
        </span>

        <span className="w-5">{open ? <IconFitlerGroupClose /> : <IconFitlerGroupOpen />}</span>
      </button>

      <div id={`filter-group-${filter.id}`} className={`filter-group px-6 ${open ? 'is-open' : ''}`}>
        <div className="space-y-2">
          <div className="h-3" aria-hidden="true" />
          {filter.children.map((childFilter) => (
            <ChildFilter key={childFilter.id} filter={childFilter} />
          ))}
          <div className="h-3" aria-hidden="true" />
        </div>
      </div>
    </div>
  )
}

function Filters({ headline, content, displayOpenCloseAll }) {
  const [openChildren, setOpenChildren] = useState([])
  const dispatch = useAppDispatch()
  const { displayFilters, filtersVisible, selectedFilters } = useAppState()

  useEffect(() => {
    if (filtersVisible && window.matchMedia('(max-width: 767px)').matches) {
      document.documentElement.classList.add('overflow-hidden')
    } else {
      document.documentElement.classList.remove('overflow-hidden')
    }
  }, [filtersVisible])

  const clearFilters = () => {
    dispatch({ type: 'clearFilters' })
  }

  const closeFilters = () => {
    dispatch({ type: 'setFiltersVisible', payload: false })

    if (window.matchMedia('(max-width: 767px)').matches) {
      dispatch({ type: 'setMapVisible', payload: false })
    }
  }

  const handleToggle = () => {
    const allChildrenOpen = openChildren.length === displayFilters.filter((x) => x.children.length).length

    if (allChildrenOpen) {
      setOpenChildren([])
    } else {
      setOpenChildren(displayFilters.filter((x) => x.children.length).map((x) => x.id))
    }
  }

  const onToggle = (filterId, isOpen) => {
    setOpenChildren((prev) => {
      const children = [...prev]

      if (isOpen) {
        children.push(filterId)
      } else {
        children.splice(children.indexOf(filterId), 1)
      }

      return children
    })
  }

  return (
    <div
      className={`filters-sidebar fixed inset-0 z-100 flex h-[100dvh] w-full flex-col bg-white pt-8 brand-shadow-sm md:absolute md:z-3 md:h-auto md:max-w-xs lg:max-w-sm xl:relative xl:w-[28vw] xl:max-w-[480px] xl:p-0 ${
        filtersVisible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      }`}
    >
      <div className="hidden p-6 lg:p-8 xl:block xl:pt-12">
        <div
          className="text-2xl xl:text-3xl 2xl:text-4xl em-bold text-green"
          dangerouslySetInnerHTML={{ __html: headline }}
        />
        <div className="text-base richtext wysiwyg" dangerouslySetInnerHTML={{ __html: content }} />
      </div>

      <div className="flex px-6 pb-4">
        {selectedFilters.length > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-sm font-semibold underline xl:text-base text-primary-hover"
          >
            Clear Filters
          </button>
        )}

        {displayOpenCloseAll && (
          <button
            onClick={handleToggle}
            className="ml-auto text-sm font-semibold underline xl:text-base text-primary-hover"
          >
            {openChildren.length === displayFilters.filter((x) => x.children.length).length ? 'Close All' : 'Open All'}
          </button>
        )}
      </div>

      <div className="overflow-scroll flex-1 pb-6 xl:text-base 2xl:text-lg overflow-scroll-touch">
        <form>
          {displayFilters.map((filter) =>
            filter.children.length ? (
              <TopLevelCategory key={filter.id} filter={filter} openChildren={openChildren} onToggle={onToggle} />
            ) : (
              <TopLevelFilter key={filter.id} filter={filter} />
            )
          )}
        </form>
      </div>

      <div className="py-8 xl:hidden">
        <div className="container">
          <button type="button" className="w-full btn btn-fill-primary" onClick={closeFilters}>
            <span>Show Results</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Filters
